/**
 * Wire contract of the WhatsApp bot's per-contact language API, reached
 * through the CRM's own Vercel proxy (src/server/contactLanguageProxy.ts):
 *
 *   GET /api/contact-language/<whatsapp_user_id>   -> ContactLanguageState
 *   PUT /api/contact-language/<whatsapp_user_id>   -> ContactLanguageState
 *       body { preferred_language: string|null, expected_updated_at: string|null }
 *
 * Refusals: 400 unsupported code, 404 unknown contact, 409 stale
 * `expected_updated_at`, 401/403 from the CRM's own session boundary, 500 when
 * the proxy has no bot URL / key. Plan 2026-09-21 §4.5, tickets LANG-109 and
 * LANG-500.
 */

/** One enabled language as the bot advertises it. */
export type SupportedLanguage = {
  /** Lowercase BCP-47 primary subtag ("it", "es", …). */
  code: string;
  english_label: string;
  endonym: string;
};

/** How the current preference was set — the `sources` enum of echo-language.v1. */
export type ContactLanguageSource =
  | "user_confirmed"
  | "user_requested"
  | "crm_admin"
  | "felix_tool"
  | "import"
  | string;

/** `b2c-language-policy.v1.mode` as the bot reports it. */
export type ContactLanguagePolicyMode = "off" | "observe" | "active" | string;

export type ContactLanguageState = {
  whatsapp_user_id: string;
  /** null = default English, never asked. */
  preferred_language: string | null;
  source: ContactLanguageSource | null;
  updated_at: string | null;
  supported_languages: SupportedLanguage[];
  policy_mode: ContactLanguagePolicyMode;
};

export type ContactLanguageErrorCode =
  | "unauthorized"
  | "forbidden"
  | "unsupported_language"
  | "unknown_contact"
  | "conflict"
  | "unconfigured"
  | "dependency_unavailable"
  | "unknown";

export class ContactLanguageRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ContactLanguageErrorCode,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ContactLanguageRequestError";
  }
}
